import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useQuery, useMutation } from "@apollo/client";
import { Plus, ChevronLeft, ChevronRight, Calendar, RotateCcw } from "lucide-react";
import { BookingProgressBar, BookingStateBadge, DataTable, EmptyState, PageHeader, type Column } from "../components/ui";
import { ManualSessionModal } from "./sessions/ManualSessionModal";
import { useToastPush } from "../components/ui/Toast";
import { GET_SESSIONS_LIST, GET_PROVIDERS, RESTORE_BOOKING } from "../apollo/sessions";
import { formatWhen, formatRelative } from "../utils/sessionTime";
import { getWeekRange, toLocalDateStr as toDateStr } from "../utils/dateRange";

// ── Types for the query shape ────────────────────────────────────────

interface BookingRow {
  id: string;
  state: string;
  method: string;
  availabilityId: string;
  primaryCustomerName: string | null;
  deletedAt: string | null;
  bookingType: { id: string; name: string } | null;
  availability: {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    canPhone: boolean;
    canVideo: boolean;
    canInPerson: boolean;
    providerId: string;
  } | null;
}

interface ProviderRow {
  id: string;
  firstName: string;
  lastName: string;
}

// ── Helpers ──────────────────────────────────────────────────────────

type StateFilter = "upcoming" | "past" | "cancelled" | "deleted" | "all";

const STATE_FILTERS: { key: StateFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "upcoming", label: "Upcoming" },
  { key: "past", label: "Past" },
  { key: "cancelled", label: "Cancelled" },
  { key: "deleted", label: "Deleted" },
];

function readStateFilter(params: URLSearchParams): StateFilter {
  const v = params.get("state");
  return STATE_FILTERS.some((f) => f.key === v) ? (v as StateFilter) : "all";
}

// ── Component ────────────────────────────────────────────────────────

export function Sessions() {
  const navigate = useNavigate();
  // null = no week filter ("All time"); number = offset from current week
  const [weekOffset, setWeekOffset] = useState<number | null>(null);
  const [providerFilter, setProviderFilter] = useState<string>("all");
  const [searchParams] = useSearchParams();
  const stateFilter = readStateFilter(searchParams);
  const [createOpen, setCreateOpen] = useState(false);

  const dateFilterActive = weekOffset !== null;
  const week = useMemo(() => getWeekRange(weekOffset ?? 0), [weekOffset]);
  const stepWeek = (delta: number) =>
    setWeekOffset((o) => (o === null ? delta : o + delta));

  const toast = useToastPush();

  // Fetch bookings — sorted by created_at desc, generous page
  // When on the Deleted tab, pass includeDeleted so the API returns soft-deleted rows
  const { data, loading, refetch } = useQuery(GET_SESSIONS_LIST, {
    variables: {
      page: 0,
      perPage: 200,
      sortField: "created_at",
      sortOrder: "DESC",
      filter: stateFilter === "deleted" ? { includeDeleted: true } : undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  const [restoreBooking] = useMutation(RESTORE_BOOKING);

  const { data: providerData } = useQuery(GET_PROVIDERS, {
    variables: { perPage: 1000, sortField: "lastName", sortOrder: "ASC" },
  });

  const allBookings: BookingRow[] = data?.allBookings ?? [];
  const totalCount: number = data?._allBookingsMeta?.count ?? 0;
  const providers: ProviderRow[] = providerData?.allProviders ?? [];

  const providerMap = useMemo(() => {
    const m = new Map<string, ProviderRow>();
    providers.forEach((l) => m.set(l.id, l));
    return m;
  }, [providers]);

  // Filter bookings client-side. The week filter only applies when the user
  // has actively engaged the date stepper (weekOffset !== null).
  const filtered = useMemo(() => {
    const startStr = toDateStr(week.start);
    const endStr = toDateStr(week.end);
    const now = new Date();

    return allBookings.filter((b) => {
      const avail = b.availability;

      // Deleted tab: show only soft-deleted bookings
      if (stateFilter === "deleted") {
        return b.deletedAt != null;
      }

      // All other tabs: skip deleted bookings (backend excludes them, but guard just in case)
      if (b.deletedAt != null) return false;

      // Week range filter — only when the date filter is active
      if (dateFilterActive && avail) {
        if (avail.date < startStr || avail.date > endStr) return false;
      }

      // Provider filter
      if (providerFilter !== "all" && avail?.providerId !== providerFilter) return false;

      // State filter
      if (stateFilter === "upcoming") {
        if (b.state === "closed" || b.state === "to_cancel" || b.state === "cancelled") return false;
        if (avail) {
          const dt = new Date(`${avail.date}T${avail.startTime}Z`);
          if (dt < now) return false;
        }
      } else if (stateFilter === "past") {
        if (avail) {
          const dt = new Date(`${avail.date}T${avail.startTime}Z`);
          if (dt >= now && b.state !== "closed") return false;
        }
      } else if (stateFilter === "cancelled") {
        if (b.state !== "to_cancel" && b.state !== "cancelled") return false;
      }

      return true;
    });
  }, [allBookings, week, providerFilter, stateFilter, dateFilterActive]);

  // Sort filtered: upcoming ASC (next session first), everything else DESC (most recent first)
  const sorted = useMemo(() => {
    const asc = stateFilter === "upcoming";
    return [...filtered].sort((a, b) => {
      const aDate = a.availability?.date ?? "";
      const bDate = b.availability?.date ?? "";
      if (aDate !== bDate) return asc ? aDate.localeCompare(bDate) : bDate.localeCompare(aDate);
      const aTime = a.availability?.startTime ?? "";
      const bTime = b.availability?.startTime ?? "";
      if (aTime !== bTime) return asc ? aTime.localeCompare(bTime) : bTime.localeCompare(aTime);
      // Fallback: createdAt DESC when availability is the same or missing
      return 0;
    });
  }, [filtered, stateFilter]);

  const handleRestore = async (bookingId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await restoreBooking({ variables: { bookingId } });
      toast({ kind: "success", title: "Session restored" });
      refetch();
    } catch {
      toast({ kind: "error", title: "Failed to restore session" });
    }
  };

  // Compute summary
  const uniqueProviders = useMemo(() => {
    const ids = new Set(sorted.map((b) => b.availability?.providerId).filter(Boolean));
    return ids.size;
  }, [sorted]);

  // Columns
  const columns: Column<BookingRow>[] = [
    {
      key: "when",
      header: "When",
      colSpan: 2,
      render: (b) => {
        const avail = b.availability;
        if (!avail) return <div className="text-[13px] text-ink-4">No time</div>;
        return (
          <div>
            <div className="text-[13px] font-medium num">
              {formatWhen(avail.date, avail.startTime)}
            </div>
            <div className="text-[11px] text-ink-4">
              {formatRelative(avail.date, avail.startTime)}
            </div>
          </div>
        );
      },
    },
    {
      key: "client",
      header: "Client",
      colSpan: 2,
      render: (b) => (
        <div className="text-[13px] font-medium truncate">
          {b.primaryCustomerName ?? "Unknown client"}
        </div>
      ),
    },
    {
      key: "matter",
      header: "Matter",
      colSpan: 2,
      hideBelow: "md",
      render: (b) => (
        <div className="text-[12.5px] text-ink-3 truncate">
          {b.bookingType?.name ?? "—"}
        </div>
      ),
    },
    {
      key: "provider",
      header: "Provider",
      colSpan: 2,
      hideBelow: "lg",
      render: (b) => {
        const provider = b.availability ? providerMap.get(b.availability.providerId) : null;
        return (
          <div className="text-[12.5px] text-ink-3 truncate">
            {provider ? `${provider.firstName} ${provider.lastName}` : "—"}
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      colSpan: 2,
      hideBelow: "md",
      render: (b) =>
        stateFilter === "deleted" ? (
          <span className="inline-flex px-2 py-0.5 rounded-full bg-canvas-2 text-ink-4 text-[11px] font-medium">
            Deleted
          </span>
        ) : (
          <BookingStateBadge state={b.state} />
        ),
    },
    {
      key: "progress",
      header: stateFilter === "deleted" ? "" : "Progress",
      colSpan: 2,
      hideBelow: "lg",
      align: "right",
      render: (b) =>
        stateFilter === "deleted" ? (
          <div className="flex justify-end">
            <button
              onClick={(e) => handleRestore(b.id, e)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium text-tide hover:bg-tide-ss transition"
            >
              <RotateCcw size={12} strokeWidth={2} />
              Restore
            </button>
          </div>
        ) : (
          <div className="flex justify-end">
            <BookingProgressBar
              state={b.state}
              availabilityDate={b.availability?.date}
              availabilityEndTime={b.availability?.endTime}
              compact
            />
          </div>
        ),
    },
  ];

  const scopeLabel = dateFilterActive ? "this week" : "shown";
  const tableFooter = (
    <div className="flex items-center justify-between">
      <span>
        {sorted.length} session{sorted.length !== 1 ? "s" : ""} {scopeLabel}
        {totalCount > 0 && ` (${totalCount} total)`}
      </span>
      <span className="num text-ink-4">
        across {uniqueProviders} provider{uniqueProviders !== 1 ? "s" : ""}
      </span>
    </div>
  );

  return (
    <div>
      <PageHeader
        tabsLabel="Session views"
        tabs={STATE_FILTERS}
        activeTab={stateFilter}
        tabParam="state"
        defaultTab="all"
        status={
          loading
            ? "Loading sessions..."
            : sorted.length > 0
              ? `${sorted.length} ${scopeLabel}, across ${uniqueProviders} provider${uniqueProviders !== 1 ? "s" : ""}.`
              : `No sessions ${scopeLabel}.`
        }
        controls={
          <>
            {/* Date range stepper */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => stepWeek(-1)}
                className="w-11 h-11 md:w-8 md:h-8 rounded-md hair border bg-paper-hi hover:bg-paper-2 flex items-center justify-center text-ink-3"
                aria-label="Previous week"
              >
                <ChevronLeft size={14} strokeWidth={2} />
              </button>
              <div
                className={`px-2 text-[13px] font-medium whitespace-nowrap ${
                  dateFilterActive ? "text-ink-2" : "text-ink-4 italic"
                }`}
              >
                {dateFilterActive ? week.label : "All time"}
              </div>
              <button
                onClick={() => stepWeek(1)}
                className="w-11 h-11 md:w-8 md:h-8 rounded-md hair border bg-paper-hi hover:bg-paper-2 flex items-center justify-center text-ink-3"
                aria-label="Next week"
              >
                <ChevronRight size={14} strokeWidth={2} />
              </button>
            </div>
            {dateFilterActive ? (
              <button
                onClick={() => setWeekOffset(null)}
                className="px-3 min-h-11 md:min-h-0 md:py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-2 hover:bg-paper-2 whitespace-nowrap"
              >
                All time
              </button>
            ) : (
              <button
                onClick={() => setWeekOffset(0)}
                className="px-3 min-h-11 md:min-h-0 md:py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-2 hover:bg-paper-2"
              >
                Today
              </button>
            )}

            {/* Provider filter */}
            <select
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              aria-label="Filter by provider"
              className="px-3 min-h-11 md:min-h-0 md:py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3 hover:bg-paper-2 appearance-none cursor-pointer pr-7"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23516680' stroke-width='2' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 8px center",
              }}
            >
              <option value="all">All providers</option>
              {providers.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.firstName} {l.lastName}
                </option>
              ))}
            </select>
          </>
        }
        actions={
          <button
            onClick={() => setCreateOpen(true)}
            className="px-3.5 py-2 rounded-md bg-tide text-paper-hi text-[13px] font-medium flex items-center gap-1.5 hover:bg-tide-d"
          >
            <Plus size={13} strokeWidth={2.2} />
            New session
          </button>
        }
        mobileAction={{
          label: "New session",
          icon: <Plus size={16} strokeWidth={2.2} />,
          onClick: () => setCreateOpen(true),
        }}
      />

      {/* Table body */}
      <div className="px-4 md:px-7 pb-20">
        {loading && sorted.length === 0 ? (
          <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
            {/* Skeleton */}
            <div className="grid grid-cols-12 gap-3 px-5 py-2.5 hair-b bg-wash text-[11px] font-semibold text-ink-4 uppercase tracking-wider">
              <div className="col-span-2">When</div>
              <div className="col-span-3">Client</div>
              <div className="col-span-2 hidden md:block">Matter</div>
              <div className="col-span-2 hidden lg:block">Provider</div>
              <div className="col-span-2 hidden md:block">Status</div>
              <div className="col-span-1 text-right">Location</div>
            </div>
            <div className="divide-hair">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="grid grid-cols-12 gap-3 px-5 py-4 items-center">
                  <div className="col-span-2">
                    <div className="h-3.5 w-24 rounded bg-canvas animate-pulse" />
                    <div className="h-2.5 w-16 rounded bg-canvas animate-pulse mt-1.5" />
                  </div>
                  <div className="col-span-3">
                    <div className="h-3.5 w-28 rounded bg-canvas animate-pulse" />
                  </div>
                  <div className="col-span-2 hidden md:block">
                    <div className="h-3 w-20 rounded bg-canvas animate-pulse" />
                  </div>
                  <div className="col-span-2 hidden lg:block">
                    <div className="h-3 w-24 rounded bg-canvas animate-pulse" />
                  </div>
                  <div className="col-span-2 hidden md:block">
                    <div className="h-3 w-16 rounded bg-canvas animate-pulse" />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <div className="h-3 w-8 rounded bg-canvas animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : sorted.length > 0 ? (
          <DataTable<BookingRow>
            columns={columns}
            data={sorted}
            footer={tableFooter}
            onRowClick={(b) => navigate(`/sessions/${b.id}`)}
            rowHighlight={stateFilter === "deleted" ? () => "opacity-70" : undefined}
          />
        ) : (
          <div className="bg-paper-hi rounded-lg shadow-card">
            <EmptyState
              icon={<Calendar size={28} strokeWidth={1.5} />}
              heading="No sessions this week"
              description="Try adjusting the date range or filters, or check another week."
            />
          </div>
        )}
      </div>

      <ManualSessionModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => refetch()}
      />
    </div>
  );
}
